/**
 * Custom hook to delete a selection associated with a specific comment ID.
 *
 * This hook provides a function that removes all `selection-mark` marks and
 * `selection-node` occurrences related to the given `commentId` from the editor's
 * current state and other loaded document parts. Cleanup stays outside undo history.
 * Changes only in other parts explicitly save because they emit no editor update.
 *
 * @param commentId - The ID of the comment whose selection is to be deleted. Defaults to `NEW_COMMENT_ID`.
 */

import { useContext } from 'react';

import { Transaction } from '@tiptap/pm/state';
import { MarkType, Node as PmNode, Slice } from '@tiptap/pm/model';

import { NEW_COMMENT_ID, SELECTION_MARK_NAME, SELECTION_NODE_NAME } from '../constants';
import { DocumentContext } from '@modules/documents';
import { removeCommentFromOtherParts, whenSwapDone } from '@modules/documents/largeDocument';

const useDeleteSelection = () => {
  const documentContext = useContext(DocumentContext);

  return (commentId = NEW_COMMENT_ID) => {
    if (!documentContext) return;
    // A part swap must finish before removing anchors and capturing the saved document.
    whenSwapDone(() => {
      const { editor, isEdit, save } = documentContext;
      if (editor.isDestroyed) return;
      const { state } = editor;

      const tr: Transaction = state.tr;
      const markType: MarkType | undefined = state.schema.marks[SELECTION_MARK_NAME];

      // 1) Remove only marks with this commentId across the whole doc
      if (markType) {
        const targetMark = markType.create({ commentId });
        tr.removeMark(0, state.doc.content.size, targetMark);
      }

      // 2) Collect and unwrap selection-node blocks with this commentId
      const nodesToUnwrap: { pos: number; node: PmNode }[] = [];
      state.doc.descendants((node, pos) => {
        if (node.type.name === SELECTION_NODE_NAME && node.attrs.commentId === commentId) {
          nodesToUnwrap.push({ pos, node });
        }
      });

      // --- Remove selection nodes from the content:

      // replace each selection-node with its content, from last to first
      // (to avoid invalidating the positions of not-yet-processed nodes).
      for (let i = nodesToUnwrap.length - 1; i >= 0; i--) {
        const { pos, node } = nodesToUnwrap[i];
        tr.replaceRange(pos, pos + node.nodeSize, new Slice(node.content, 0, 0));
      }

      if (tr.docChanged) {
        editor.view.dispatch(tr.setMeta('addToHistory', false));
      }

      // The transaction above reaches only the part the editor holds; the same comment can be anchored
      // in any other part, and nothing else would ever take it out.
      const changedOtherParts = removeCommentFromOtherParts(commentId);
      if (changedOtherParts && isEdit) void save({ forceSave: true });
    });
  };
};

export default useDeleteSelection;
